import HoverCard from "@/v2-ui/hoverCard"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-backoffice-client/components/button"
import { pluralize } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"

/** One pending edit, as the footer lists it. */
export type LicenseFeatureGroupChange = {
  kind: "added" | "removed" | "changed"
  label: string
  /** Where it is, or what it went from and to. */
  detail?: string
}

export type LicenseFeatureGroupComposeFooterProps = {
  changes: readonly LicenseFeatureGroupChange[]
  isSaving: boolean
  onReset(): void
  onSave(): void
  /** How the host places it: pinned by the layout, or sticky at the foot of a flowing page. */
  className?: string
}

const CHANGE_GLYPH: Record<LicenseFeatureGroupChange["kind"], string> = {
  added: "+",
  removed: "−",
  changed: "→",
}

const LISTED_CHANGES_MAX = 14

// The one place the package is written from. Stays in view below the
// scrolling sections, counts the pending edits — hover the count for the
// list, so what Save will do is readable before pressing it — and offers the
// two honest exits: write them all, or drop them all.
function LicenseFeatureGroupComposeFooter(
  props: LicenseFeatureGroupComposeFooterProps,
) {
  const { changes, isSaving, onReset, onSave, className } = props
  const isDirty = changes.length > 0
  const listed = changes.slice(0, LISTED_CHANGES_MAX)

  return (
    <footer className={cn("flex items-center gap-3 border-t pt-3", className)}>
      {isDirty ? (
        <HoverCard openDelay={150} closeDelay={100}>
          <HoverCard.Trigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded px-1 text-xs text-primary underline-offset-2 hover:underline"
              data-feature-code="UI_BackofficeFeatureGroup_Compose_Changes"
            >
              <span className="size-1.5 rounded-full bg-primary" />
              {pluralize(changes.length, "unsaved change")} — nothing is written
              until Save
            </button>
          </HoverCard.Trigger>
          <HoverCard.Content side="top" align="start" className="w-80 text-xs">
            <ul className="flex flex-col gap-1">
              {listed.map((change) => (
                <li
                  key={`${change.kind}:${change.label}:${change.detail ?? ""}`}
                  className="flex items-baseline gap-2"
                >
                  <span
                    className={cn(
                      "w-3 shrink-0 text-center font-mono",
                      change.kind === "removed"
                        ? "text-destructive"
                        : "text-primary",
                    )}
                  >
                    {CHANGE_GLYPH[change.kind]}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {change.label}
                    {change.detail && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {change.detail}
                      </span>
                    )}
                  </span>
                </li>
              ))}
              {changes.length > listed.length && (
                <li className="text-muted-foreground">
                  and {changes.length - listed.length} more
                </li>
              )}
            </ul>
          </HoverCard.Content>
        </HoverCard>
      ) : (
        <p className="text-xs text-muted-foreground">
          Everything shown is saved.
        </p>
      )}
      <div className="flex-1" />
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!isDirty || isSaving}
        data-feature-code="UI_BackofficeFeatureGroup_Compose_Reset"
        onClick={onReset}
      >
        Reset
      </Button>
      <Button
        type="button"
        size="sm"
        isLoading={isSaving}
        disabled={!isDirty}
        data-feature-code="UI_BackofficeFeatureGroup_Compose_Save"
        onClick={onSave}
      >
        Save
      </Button>
    </footer>
  )
}

export default LicenseFeatureGroupComposeFooter
